export type PreviewKind =
  | "pdf"
  | "image"
  | "video"
  | "audio"
  | "text"
  | "csv"
  | "json"
  | "html"
  | "docx"
  | "xlsx"
  | "doc"
  | "xls"
  | "zip"
  | "unknown";

const EXT_TO_KIND: Record<string, PreviewKind> = {
  pdf: "pdf",

  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  svg: "image",
  bmp: "image",
  ico: "image",
  avif: "image",

  mp4: "video",
  webm: "video",
  mov: "video",
  m4v: "video",
  ogv: "video",

  mp3: "audio",
  wav: "audio",
  ogg: "audio",
  oga: "audio",
  m4a: "audio",
  flac: "audio",
  aac: "audio",

  txt: "text",
  log: "text",
  md: "text",
  ini: "text",
  conf: "text",
  yml: "text",
  yaml: "text",
  xml: "text",
  rtf: "text",
  tsv: "text",

  csv: "csv",

  json: "json",
  geojson: "json",

  html: "html",
  htm: "html",

  docx: "docx",
  xlsx: "xlsx",
  doc: "doc",
  xls: "xls",

  zip: "zip",
};

const MIME_TO_KIND: Array<{ test: RegExp; kind: PreviewKind }> = [
  { test: /^application\/pdf$/i, kind: "pdf" },
  { test: /^image\//i, kind: "image" },
  { test: /^video\//i, kind: "video" },
  { test: /^audio\//i, kind: "audio" },
  { test: /^application\/json$/i, kind: "json" },
  { test: /^text\/csv$/i, kind: "csv" },
  { test: /^text\/html$/i, kind: "html" },
  {
    test: /^application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document$/i,
    kind: "docx",
  },
  {
    test: /^application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet$/i,
    kind: "xlsx",
  },
  { test: /^application\/msword$/i, kind: "doc" },
  { test: /^application\/vnd\.ms-excel$/i, kind: "xls" },
  { test: /^application\/zip$/i, kind: "zip" },
  { test: /^application\/x-zip-compressed$/i, kind: "zip" },
  { test: /^text\//i, kind: "text" },
];

export function getExtension(name: string): string {
  const idx = name.lastIndexOf(".");
  if (idx < 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}

export function detectKind(name: string, mime?: string): PreviewKind {
  const ext = getExtension(name);
  if (ext && EXT_TO_KIND[ext]) return EXT_TO_KIND[ext];
  if (mime) {
    for (const rule of MIME_TO_KIND) {
      if (rule.test.test(mime)) return rule.kind;
    }
  }
  return "unknown";
}

export function isPreviewable(kind: PreviewKind): boolean {
  return kind !== "unknown" && kind !== "doc" && kind !== "xls";
}

export function humanKindLabel(kind: PreviewKind, ext?: string): string {
  switch (kind) {
    case "pdf":
      return "PDF document";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "text":
      return "Text";
    case "csv":
      return "CSV";
    case "json":
      return "JSON";
    case "html":
      return "HTML";
    case "docx":
      return "Word document";
    case "xlsx":
      return "Excel spreadsheet";
    case "doc":
      return "Legacy Word document (.doc)";
    case "xls":
      return "Legacy Excel spreadsheet (.xls)";
    case "zip":
      return "ZIP archive";
    default:
      return ext ? ext.toUpperCase() + " file" : "File";
  }
}

export function formatBytes(bytes?: number): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}
